import React from 'react'
import { View, TouchableOpacity, StyleProp, ViewStyle } from 'react-native'
import Clipboard from '@react-native-clipboard/clipboard'
import { Control, Controller } from 'react-hook-form'
import { useStyleSheet, StyleService } from '@src/style/service'
import { Button, Icon, Input, Text } from '@components/base'
import { Share, SMS, useSnack, Validators } from '@utils'

interface ReferralCardProps {
  referralUrl: string
  sendInvite: () => void
  message?: string
  control: Control<{ email: string }>
  showReferralUrl?: boolean
  style?: StyleProp<ViewStyle>
  backgroundColor?: string
}

export const ReferralCard = (props: ReferralCardProps) => {
  const {
    referralUrl,
    showReferralUrl,
    sendInvite,
    message,
    control,
    style,
    backgroundColor,
  } = props
  const styles = useStyleSheet(themedStyle)

  const showSnack = useSnack()

  const sendMessage = (url: string) => {
    SMS.sms({ message: `${message} ${url}` })
  }

  const share = (url: string) => {
    Share.share({ url, message })
  }

  const copy = (text: string) => {
    Clipboard.setString(text)
    showSnack('Link copied!')
  }

  return (
    <View style={[styles.card, style]}>
      <View style={styles.sendInviteContainer}>
        <Controller
          control={control}
          name="email"
          rules={{ required: true, validate: Validators.EmailValidator }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              accessibilityLabel="Email"
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              hasError={!!fieldState.error}
              label="Email"
              placeholder="Enter email address"
              returnKeyType="done"
              onSubmitEditing={sendInvite}
            />
          )}
        />
        <Button
          type="primary"
          size="large"
          style={styles.sendInviteButton}
          onPress={sendInvite}
          accessibilityLabel="Send invite"
        >
          Send Invite
        </Button>
      </View>
      <View style={styles.dividerContainer}>
        <View style={styles.divider} />
        <Text
          type="regular"
          style={[styles.dividerText, backgroundColor ? { backgroundColor } : {}]}
        >
          or
        </Text>
      </View>
      <View style={styles.shareContainer}>
        <Text type="large" bold style={styles.shareText}>
          Share your link
        </Text>
        {!!referralUrl && (
          <>
            {showReferralUrl && (
              <Text type="regular" style={styles.link}>
                {referralUrl}
              </Text>
            )}
            <View style={styles.buttonsContainer}>
              <TouchableOpacity
                accessibilityLabel="Text"
                style={styles.iconButton}
                onPress={() => sendMessage(referralUrl)}
              >
                <Icon name="chat-circle-text" size={32} />
                <Text type="regular" style={styles.iconText}>
                  Text
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                accessibilityLabel="Copy"
                style={styles.iconButton}
                onPress={() => copy(referralUrl)}
              >
                <Icon name="link-simple-horizontal" size={32} />
                <Text type="regular" style={styles.iconText}>
                  Copy
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                accessibilityLabel="More"
                style={styles.iconButton}
                onPress={() => share(referralUrl)}
              >
                <Icon name="dots-three" size={32} />
                <Text type="regular" style={styles.iconText}>
                  More
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  card: {
    marginTop: 24,
    paddingVertical: 24,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'theme.surface.base1',
  },
  sendInviteButton: {
    alignSelf: 'stretch',
    marginTop: 24,
  },
  shareText: {
    textAlign: 'center',
  },
  link: {
    marginTop: 8,
    textAlign: 'center',
    color: 'theme.text.link',
  },
  buttonsContainer: {
    marginTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconButton: {
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
  },
  iconText: {
    marginTop: 8,
    textAlign: 'center',
  },
  dividerContainer: {
    marginVertical: 24,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: 'theme.surface.base1',
  },
  dividerText: {
    position: 'absolute',
    textAlign: 'center',
    backgroundColor: 'theme.background',
    paddingHorizontal: 8,
  },
  sendInviteContainer: {
    paddingHorizontal: 24,
  },
  shareContainer: {
    paddingHorizontal: 24,
  },
})
